import { ChangeDetectorRef, Component, EventEmitter, HostListener, NgZone, OnInit, Output } from '@angular/core';
import {
  VlAuctionReadingLot,
  VlAuctionReadingsResponse,
  VlAuctionSummary,
  VlResearchDecision,
} from 'src/app/models/valuationLab';
import { ValuationLabService, VlError } from 'src/app/services/valuation-lab-service';
import { RESEARCH_CATEGORIES } from '../research/vlab-research.component';

const PAGE_SIZE = 100;
const HOUSE_LABELS: Record<string, string> = {
  dvla: 'DVLA',
  collecting_cars: 'Collecting Cars',
  brightwells: 'Brightwells',
  bonhams: 'Bonhams',
  iconic: 'Iconic',
  mathewsons: 'Mathewsons',
};

/** Inline state for one lot's actions. */
interface RowState {
  open: 'correct' | 'why' | null;
  readsAs: string;
  category: string;
  why: string;
  saving: boolean;
  saved: string | null;
  error: string | null;
}

/**
 * Valuation Lab · Upcoming auction — lots in the next auctions with a quick
 * pre-sale "reading" of what each plate means, for the owner to accept /
 * correct / reject, or explain / send to research. Accepted and rejected
 * readings feed every later reading pass.
 */
@Component({
  selector: 'pla-vlab-upcoming',
  templateUrl: './vlab-upcoming.component.html',
  styleUrls: ['../admin-valuation-lab.component.scss', './vlab-upcoming.component.scss'],
})
export class VlabUpcomingComponent implements OnInit {
  /** readings not yet reviewed — shown on the parent's view switch */
  @Output() pending = new EventEmitter<number>();

  public isPhone = false;
  public readonly categories = RESEARCH_CATEGORIES;

  public house = '';
  public hasReading: 'yes' | 'no' | '' = 'yes';
  public review: 'pending' | 'done' | '' = 'pending';
  public q = '';
  public page = 1;

  public data: VlAuctionReadingsResponse | null = null;
  public lots: VlAuctionReadingLot[] = [];
  public total = 0;
  public loading = false;
  public error: string | null = null;
  public rows: Record<string, RowState> = {};
  private seq = 0;

  constructor(private lab: ValuationLabService, private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.onResize();
    this.load(1);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window === 'undefined') return;
    this.isPhone = window.innerWidth < 768;
  }

  // ===================================================================== data

  public load(page: number): void {
    const seq = ++this.seq;
    this.loading = true;
    this.error = null;
    this.lab
      .auctionReadings({
        house: this.house || undefined,
        has_reading: this.hasReading || undefined,
        review: this.review || undefined,
        q: this.q.trim() ? this.q.toUpperCase().replace(/[^A-Z0-9]/g, '') : undefined,
        page,
        page_size: PAGE_SIZE,
      })
      .then((r) =>
        this.apply(() => {
          if (seq !== this.seq) return;
          this.data = r;
          this.lots = (r && r.lots) || [];
          this.total = r && typeof r.total === 'number' ? r.total : this.lots.length;
          this.page = (r && r.page) || page;
          this.emitPending();
        })
      )
      .catch((e) =>
        this.apply(() => {
          if (seq !== this.seq) return;
          this.lots = [];
          this.error = errText(e);
        })
      )
      .finally(() => this.apply(() => seq === this.seq && (this.loading = false)));
  }

  public onFilterChange(): void {
    this.load(1);
  }

  public get pages(): number {
    return Math.max(1, Math.ceil(this.total / PAGE_SIZE));
  }

  public get auctions(): { key: string; label: string; s: VlAuctionSummary }[] {
    const a = (this.data && this.data.auctions) || {};
    return Object.keys(a).map((k) => ({ key: k, label: houseLabel(k), s: a[k] }));
  }

  private emitPending(): void {
    const c = this.data && this.data.counts;
    if (c) this.pending.emit(Math.max(0, (c.with_reading || 0) - (c.reviewed || 0)));
  }

  private refreshCounts(): void {
    this.lab
      .auctionReadings({ page: 1, page_size: 1 })
      .then((r) =>
        this.apply(() => {
          if (!r || !this.data) return;
          this.data = { ...this.data, counts: r.counts, auctions: r.auctions || this.data.auctions };
          this.emitPending();
        })
      )
      .catch(() => {});
  }

  // ================================================================== actions

  public row(l: VlAuctionReadingLot): RowState {
    if (!this.rows[l.key]) {
      this.rows[l.key] = { open: null, readsAs: '', category: '', why: '', saving: false, saved: null, error: null };
    }
    return this.rows[l.key];
  }

  public open(l: VlAuctionReadingLot, what: 'correct' | 'why'): void {
    const r = this.row(l);
    if (r.open === what) {
      r.open = null;
      return;
    }
    r.open = what;
    r.error = null;
    if (what === 'correct') {
      r.readsAs = (l.reading && l.reading.reads_as) || '';
      r.category = (l.reading && l.reading.category) || '';
    } else {
      r.why = (l.owner_note && l.owner_note.why) || '';
    }
    setTimeout(() => {
      const el = typeof document !== 'undefined' ? document.getElementById((what === 'why' ? 'uw-' : 'uc-') + l.key) : null;
      if (el) el.focus();
    }, 0);
  }

  public decide(l: VlAuctionReadingLot, decision: VlResearchDecision, corrected = false): void {
    const r = this.row(l);
    if (r.saving) return;
    const body: { plate: string; decision: VlResearchDecision; reads_as?: string; category?: string } = {
      plate: l.registration,
      decision,
    };
    if (corrected) {
      if (!r.readsAs.trim()) {
        r.error = 'Type what it reads as.';
        return;
      }
      body.reads_as = r.readsAs.trim();
      if (r.category) body.category = r.category;
    }
    this.run(r, this.lab.researchReview(body), () => {
      l.review = {
        decision,
        reads_as: body.reads_as || null,
        category: body.category || null,
        note: null,
        at: new Date().toISOString(),
        by: 'you',
      };
      r.open = null;
      r.saved = corrected ? 'Corrected · accepted' : decision === 'accepted' ? 'Accepted' : 'Rejected';
      this.refreshCounts();
    });
  }

  public saveWhy(l: VlAuctionReadingLot): void {
    const r = this.row(l);
    const why = r.why.trim();
    if (!why || r.saving) return;
    this.run(r, this.lab.researchNotes([{ plate: l.registration, why }]), (res) => {
      if (res && res.skipped && res.skipped.indexOf(l.registration) >= 0) {
        r.error = 'Not saved — the server skipped this plate.';
        return;
      }
      l.owner_note = { why, category: null, at: new Date().toISOString(), by: 'you' };
      r.open = null;
      r.saved = 'Note saved';
    });
  }

  public sendToResearch(l: VlAuctionReadingLot): void {
    const r = this.row(l);
    if (r.saving) return;
    this.run(r, this.lab.researchNotes([{ plate: l.registration, send_to_research: true }]), (res) => {
      if (res && res.skipped && res.skipped.indexOf(l.registration) >= 0) {
        r.error = 'Not sent — the server skipped this plate.';
        return;
      }
      l.research_requested = { at: new Date().toISOString(), by: 'you' };
      r.open = null;
      r.saved = 'Sent to research';
    });
  }

  /** Ctrl/Cmd+Enter in an inline box saves it. */
  public onKey(e: KeyboardEvent, l: VlAuctionReadingLot, what: 'correct' | 'why'): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (what === 'why') this.saveWhy(l);
      else this.decide(l, 'accepted', true);
    } else if (e.key === 'Escape') {
      this.row(l).open = null;
    }
  }

  private run<T>(r: RowState, p: Promise<T>, done: (res: T) => void): void {
    r.saving = true;
    r.error = null;
    r.saved = null;
    p.then((res) => this.apply(() => done(res)))
      .catch((e) => this.apply(() => (r.error = errText(e))))
      .finally(() => this.apply(() => (r.saving = false)));
  }

  // =============================================================== formatting

  public houseLabel(h: string): string {
    return houseLabel(h);
  }

  public categoryLabel(c: string | null | undefined): string {
    if (!c) return '';
    const hit = RESEARCH_CATEGORIES.find((x) => x.id === c);
    return hit ? hit.label : c.replace(/_/g, ' ');
  }

  public decisionLabel(d: string | null | undefined): string {
    return d === 'accepted' ? 'Accepted' : d === 'rejected' ? 'Rejected' : d === 'needs_more' ? 'Needs more' : d || '';
  }

  /** "24 Nov 11:00–17:39" (UK time), or "21 Oct 18:44 – 28 Oct 19:44". */
  public closesRange(s: VlAuctionSummary): string {
    const a = s.first_close ? new Date(s.first_close) : null;
    const b = s.last_close ? new Date(s.last_close) : null;
    if (!a || isNaN(a.getTime())) return '—';
    if (!b || isNaN(b.getTime())) return ukDay(a) + ' ' + ukTime(a);
    if (ukDay(a) === ukDay(b)) return ukDay(a) + ' ' + ukTime(a) + '–' + ukTime(b);
    return ukDay(a) + ' ' + ukTime(a) + ' – ' + ukDay(b) + ' ' + ukTime(b);
  }

  public closes(l: VlAuctionReadingLot): string {
    const d = l.closes_at ? new Date(l.closes_at) : null;
    if (!d || isNaN(d.getTime())) return l.status === 'soon' ? 'closing soon' : l.status || '—';
    return ukDay(d) + ' ' + ukTime(d);
  }

  public trackLot(_: number, l: VlAuctionReadingLot): string {
    return l.key;
  }

  private apply(fn: () => any): void {
    this.zone.run(() => {
      fn();
      this.cdr.markForCheck();
    });
  }
}

function houseLabel(h: string): string {
  if (HOUSE_LABELS[h]) return HOUSE_LABELS[h];
  const t = (h || '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function ukParts(d: Date, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', ...opts }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-GB', opts).format(d);
  }
}

function ukDay(d: Date): string {
  return ukParts(d, { day: 'numeric', month: 'short' });
}

function ukTime(d: Date): string {
  return ukParts(d, { hour: '2-digit', minute: '2-digit', hour12: false });
}

function errText(e: any): string {
  return e instanceof VlError ? e.message : 'Valuation Lab data unavailable';
}
